/**
 * POS Exception Zod Schemas (F01)
 *
 * Type-safe schemas for pos_transactions, pos_exceptions, pos_detection_rules.
 *
 * @module services/schemas/posSchemas
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const POSTransactionTypeSchema = z.enum([
  'sale', 'return', 'void', 'no_sale', 'discount', 'price_override',
]);
export type POSTransactionType = z.infer<typeof POSTransactionTypeSchema>;

export const POSExceptionTypeSchema = z.enum([
  'high_void', 'high_refund', 'no_sale_pattern', 'sweet_hearting',
  'under_ring', 'discount_abuse', 'post_void', 'price_override_pattern',
  'short_ring', 'pass_around', 'quantity_manipulation',
]);
export type POSExceptionType = z.infer<typeof POSExceptionTypeSchema>;

export const POSSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type POSSeverity = z.infer<typeof POSSeveritySchema>;

export const POSExceptionStatusSchema = z.enum(['open', 'investigating', 'resolved', 'dismissed']);
export type POSExceptionStatus = z.infer<typeof POSExceptionStatusSchema>;

// ============================================
// NUMERIC HELPER
// ============================================

const numericString = z.union([z.number(), z.string()]).nullable().transform(v =>
  v === null || v === undefined ? 0 : Number(v)
);

// ============================================
// UUID HELPER (accepts non-RFC-4122 UUIDs from DB)
// ============================================

const dbUuid = z.string().regex(
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
);

// ============================================
// DB ROW SCHEMAS
// ============================================

export const POSTransactionDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  store_id: dbUuid,
  register_id: z.string(),
  cashier_id: dbUuid.nullable(),
  transaction_type: POSTransactionTypeSchema,
  original_transaction_id: dbUuid.nullable(),
  subtotal: numericString,
  discount_amount: numericString,
  tax_amount: numericString,
  total: numericString,
  payment_method: z.string(),
  line_items: z.any(),
  pos_timestamp: z.string(),
  ingested_at: z.string().nullable(),
  metadata: z.any().nullable(),
  // LP fields (v5.32.0)
  business_date: z.string().nullable().optional(),
  cashier_shift_id: z.string().nullable().optional(),
  coupon_discount_amount: numericString.optional(),
  employee_discount_amount: numericString.optional(),
  is_voided: z.boolean().nullable().optional(),
  void_reason_code: z.string().nullable().optional(),
  void_authorized_by: dbUuid.nullable().optional(),
  return_reason_code: z.string().nullable().optional(),
  is_training_mode: z.boolean().nullable().optional(),
});
export type POSTransactionRow = z.infer<typeof POSTransactionDbSchema>;

export const POSExceptionDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  store_id: dbUuid,
  transaction_id: dbUuid,
  cashier_id: dbUuid.nullable(),
  exception_type: POSExceptionTypeSchema,
  severity: POSSeveritySchema,
  financial_impact: numericString,
  detection_rule_id: dbUuid.nullable(),
  status: POSExceptionStatusSchema,
  assigned_to: dbUuid.nullable(),
  evidence: z.any().nullable(),
  detected_at: z.string(),
  resolved_at: z.string().nullable(),
  resolution_notes: z.string().nullable(),
});
export type POSExceptionRow = z.infer<typeof POSExceptionDbSchema>;

export const POSDetectionRuleDbSchema = z.object({
  id: dbUuid,
  tenant_id: dbUuid,
  name: z.string(),
  description: z.string().nullable(),
  exception_type: POSExceptionTypeSchema,
  conditions: z.any(),
  severity: POSSeveritySchema,
  is_active: z.boolean(),
  created_at: z.string(),
});
export type POSDetectionRuleRow = z.infer<typeof POSDetectionRuleDbSchema>;

// ============================================
// FILTER / INPUT SCHEMAS
// ============================================

export const POSTransactionFiltersSchema = z.object({
  storeId: dbUuid.optional(),
  cashierId: dbUuid.optional(),
  transactionType: POSTransactionTypeSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});
export type POSTransactionFilters = z.infer<typeof POSTransactionFiltersSchema>;

export const POSExceptionFiltersSchema = z.object({
  storeId: dbUuid.optional(),
  cashierId: dbUuid.optional(),
  exceptionType: POSExceptionTypeSchema.optional(),
  severity: POSSeveritySchema.optional(),
  status: POSExceptionStatusSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});
export type POSExceptionFilters = z.infer<typeof POSExceptionFiltersSchema>;

export const ResolveExceptionInputSchema = z.object({
  status: z.enum(['resolved', 'dismissed']),
  notes: z.string().min(1, 'Resolution notes required'),
});
export type ResolveExceptionInput = z.infer<typeof ResolveExceptionInputSchema>;

export const DetectionRuleInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  exception_type: POSExceptionTypeSchema,
  conditions: z.record(z.string(), z.unknown()),
  severity: POSSeveritySchema,
  is_active: z.boolean().default(true),
});
export type DetectionRuleInput = z.infer<typeof DetectionRuleInputSchema>;

// ============================================
// OUTPUT / VIEW TYPES
// ============================================

export interface POSExceptionPage {
  data: POSExceptionRow[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface POSTransactionPage {
  data: POSTransactionRow[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface POSCashierRiskProfile {
  cashierId: string;
  cashierName: string;
  storeId: string;
  riskScore: number;
  totalExceptions: number;
  openExceptions: number;
  totalFinancialImpact: number;
  exceptionsByType: Record<string, number>;
  voidRate: number;
  refundRate: number;
}

export interface POSTrendPoint {
  date: string;
  count: number;
  financialImpact: number;
}

export interface POSKPISummary {
  totalExceptions: number;
  openExceptions: number;
  totalFinancialExposure: number;
  exceptionRate: number;
  resolvedCount: number;
  avgResolutionHours: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
}

// ============================================
// EXCEPTION TYPE LABELS (Spanish)
// ============================================

export const EXCEPTION_TYPE_LABELS: Record<POSExceptionType, string> = {
  high_void: 'Anulaciones Excesivas',
  high_refund: 'Devoluciones Excesivas',
  no_sale_pattern: 'Patrón No Venta',
  sweet_hearting: 'Sweet-Hearting',
  under_ring: 'Sub-Registro',
  discount_abuse: 'Abuso de Descuento',
  post_void: 'Anulación Posterior',
  price_override_pattern: 'Modificación de Precio',
  short_ring: 'Registro Incompleto',
  pass_around: 'Paso sin Registro',
  quantity_manipulation: 'Manipulación Cantidad',
};

export const SEVERITY_LABELS: Record<POSSeverity, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
};

export const STATUS_LABELS: Record<POSExceptionStatus, string> = {
  open: 'Abierta',
  investigating: 'En Investigación',
  resolved: 'Resuelta',
  dismissed: 'Descartada',
};
